import { useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Clock, Crown, AlertCircle, Settings, Loader2, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface TrialBannerProps {
  isTrialActive: boolean;
  isPremium: boolean;
  daysRemaining: number;
  planType?: "free" | "trial" | "premium";
  onUpgrade: () => void;
  onStartTrial?: () => void;
}

export function TrialBanner({ isTrialActive, isPremium, daysRemaining, planType, onUpgrade, onStartTrial }: TrialBannerProps) {
  const [portalLoading, setPortalLoading] = useState(false);

  const handleManage = async () => {
    try {
      setPortalLoading(true);
      const { data, error } = await supabase.functions.invoke("academy-customer-portal");
      if (error) throw error;
      if (!data?.url) throw new Error("URL do portal não retornada");
      window.open(data.url, "_blank");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erro desconhecido";
      toast({
        title: "Erro ao abrir portal",
        description: msg,
        variant: "destructive",
      });
    } finally {
      setPortalLoading(false);
    }
  };

  // Premium users - show manage subscription button
  if (isPremium) {
    return (
      <Alert className="bg-gradient-to-r from-primary/10 to-purple-500/10 border-primary/30">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
              <Crown className="h-5 w-5 text-primary" />
            </div>
            <AlertDescription className="text-sm">
              <span className="font-semibold text-foreground">PRO Ativo</span>
              <span className="text-muted-foreground ml-2">
                Aproveite acesso completo à Academy
              </span>
            </AlertDescription>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleManage}
            disabled={portalLoading}
          >
            {portalLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Settings className="h-4 w-4 mr-2" />
            )}
            Gerenciar assinatura
          </Button>
        </div>
      </Alert>
    );
  }

  // Trial active
  if (isTrialActive) {
    return (
      <Alert className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <AlertDescription className="text-sm">
              <span className="font-semibold text-foreground">Trial Gratuito</span>
              <span className="text-muted-foreground ml-2">
                {daysRemaining} {daysRemaining === 1 ? 'dia restante' : 'dias restantes'}
              </span>
            </AlertDescription>
          </div>
          <Button 
            size="sm" 
            onClick={onUpgrade}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
          >
            <Crown className="h-4 w-4 mr-2" />
            Fazer Upgrade
          </Button>
        </div>
      </Alert>
    );
  }

  // Trial expirado ou free sem trial iniciado
  const isFreeNeverStarted = planType === "free" && !isTrialActive && onStartTrial;

  return (
    <Alert
      className={
        isFreeNeverStarted
          ? "bg-gradient-to-r from-primary/10 to-primary/5 border-primary/30"
          : "bg-gradient-to-r from-red-500/10 to-rose-500/10 border-red-500/30"
      }
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div
            className={`h-10 w-10 rounded-full flex items-center justify-center ${
              isFreeNeverStarted ? "bg-primary/20" : "bg-red-500/20"
            }`}
          >
            {isFreeNeverStarted ? (
              <Sparkles className="h-5 w-5 text-primary" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-600" />
            )}
          </div>
          <AlertDescription className="text-sm">
            <span className="font-semibold text-foreground">
              {isFreeNeverStarted ? "Ative seu trial gratuito de 7 dias" : "Trial Expirado"}
            </span>
            <span className="text-muted-foreground ml-2">
              {isFreeNeverStarted
                ? "Sem cartão, sem cobrança. Acesse o 1º módulo de cada trilha."
                : "Assine para continuar praticando"}
            </span>
          </AlertDescription>
        </div>
        {isFreeNeverStarted ? (
          <Button size="sm" onClick={onStartTrial} className="gap-1.5">
            <Sparkles className="h-4 w-4" />
            Iniciar meu trial
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={onUpgrade}
            className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
          >
            <Crown className="h-4 w-4 mr-2" />
            Assinar Agora
          </Button>
        )}
      </div>
    </Alert>
  );
}
